import api from "../../config/api";
import LoggerService from "../../services/LoggerService";

const state = {
    email: null,
    firstname: null,
    loading: false,
    error: null,
}

const mutations = {
    SET_EMAIL(state, payload) {
        state.email = payload;
    },
    SET_FIRSTNAME(state, payload) {
        state.firstname = payload;
    },
    SET_LOADING(state, payload) {
        state.loading = payload;
    },
    SET_ERROR(state, payload) {
        state.error = payload;
    },
    CLEAR_EMAIL(state) {
        state.email = null;
    },
    CLEAR_ALL(state) {
        state.email = null;
        state.firstname = null;
        state.error = null;
    },
}

const actions = {
    setEmail({ commit }, email) {
        commit('SET_EMAIL', email);
    },
    clearEmail({ commit }) {
        commit('CLEAR_EMAIL');
    },
    clearAll({ commit }) {
        commit('CLEAR_ALL');
    },
    
    async forgotPassword({ commit }, { email, firstname }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.post('/auth/forgot-password', {
                email,
                firstname
            });
            
            commit("SET_EMAIL", email);
            
            // If API returns firstname, use it; otherwise use provided firstname
            if (response.data?.firstname) {
                commit("SET_FIRSTNAME", response.data.firstname);
            } else {
                commit("SET_FIRSTNAME", firstname);
            }
            
            commit("SET_LOADING", false);
            
            // Log successful forgot password request
            await LoggerService.logAuthAttempt(
                'FORGOT_PASSWORD',
                email,
                true,
                null,
                { firstname }
            );
            
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to send OTP. Please try again.";
            commit("SET_ERROR", errorMessage);
            commit("SET_LOADING", false);
            
            // Log failed forgot password attempt
            await LoggerService.logAuthAttempt(
                'FORGOT_PASSWORD',
                email,
                false,
                errorMessage,
                { firstname }
            );
            
            throw error;
        }
    },
    
    async verifyOtp({ commit, state }, otp) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            await api.post('/auth/verify-otp', {
                email: state.email,
                otp: otp
            });
            
            commit("SET_LOADING", false);
            
            // Log successful OTP verification
            await LoggerService.logAuthAttempt(
                'VERIFY_OTP',
                state.email,
                true,
                null,
                { otpLength: otp.length }
            );
            
            return true;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Invalid OTP. Please try again.";
            commit("SET_ERROR", errorMessage);
            commit("SET_LOADING", false);
            
            // Log failed OTP verification
            await LoggerService.logAuthAttempt(
                'VERIFY_OTP',
                state.email,
                false,
                errorMessage
            );
            
            throw error;
        }
    },
    
    async resetPassword({ commit, dispatch, state }, { otp, password, password_confirmation }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.post('/auth/reset-password', {
                email: state.email,
                otp: otp,
                password: password,
                password_confirmation: password_confirmation
            });
            
            const email = state.email;
            const output = response.data?.output;
            const loggedIn = !!output?.access_token;

            // The backend signs the user in after a successful reset
            if (loggedIn) {
                commit("SET_USER", output.user || null);
                commit("SET_TOKEN", output.access_token);
                commit("SET_SESSION_DATA", output.session_details || null);
            }

            commit("SET_LOADING", false);
            
            // Log successful password reset
            await LoggerService.logAuthAttempt(
                'RESET_PASSWORD',
                email,
                true,
                null,
                { autoLogin: loggedIn }
            );

            if (loggedIn) {
                try {
                    await dispatch('fetchHelpText');
                } catch (helpTextError) {
                    console.error('Failed to cache help text after password reset login:', helpTextError);
                }
            }
            
            commit("CLEAR_ALL");
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to reset password. Please try again.";
            commit("SET_ERROR", errorMessage);
            commit("SET_LOADING", false);
            
            // Log failed password reset
            await LoggerService.logAuthAttempt(
                'RESET_PASSWORD',
                state.email,
                false,
                errorMessage
            );
            
            throw error;
        }
    },
}

const getters = {
    stResetPasswordEmail: state => state.email,
    stResetPasswordFirstname: state => state.firstname,
    stResetPasswordLoading: state => state.loading,
    stResetPasswordError: state => state.error,
}

export default {
    state,
    mutations,
    actions,
    getters
}

